import { useState } from "react";
import { Link, useParams } from "react-router";
import { CopyButton } from "../components/CopyButton";
import { Funnel } from "../components/Funnel";
import { SourceTable } from "../components/SourceTable";
import { TrendChart } from "../components/TrendChart";
import {
  Alert,
  Card,
  CardHeader,
  LoadingBlock,
  PageHeader,
  Select,
  Tag,
} from "../components/ui";
import { AFFILIATE_SERIES } from "../lib/insights";
import { useLinkInsight } from "../lib/queries";
import { useTranslation } from "react-i18next";

const WINDOWS = [7, 30, 90, 365];

/**
 * One campaign, and whether it is working.
 *
 * The link is the unit an affiliate campaigns with — one per placement, one per
 * newsletter, one per talk — so it is the unit everything here measures. That
 * is also the honest limit of what this data can say: a signup arrives from the
 * platform with a code and no visitor identity, so a conversion can be traced
 * to a *link* and never to the site the click came from. An affiliate who wants
 * to compare two placements makes two links, and the empty state on the traffic
 * screen says so.
 *
 * Three questions, in the order somebody asks them:
 *
 * 1. **Is the traffic any good?** The funnel — how many of the clicks signed
 *    up, how many of those paid, and what a click was worth.
 * 2. **Where did it come from?** The sources, which is what an affiliate acts
 *    on: more of the site that worked, less of the one that did not.
 * 3. **Is it still working?** The trend, because a campaign that earned well
 *    three months ago and nothing since looks identical to a healthy one in any
 *    lifetime total.
 */
export function LinkPage() {
  const { t } = useTranslation("links");
  const { linkId } = useParams();
  const [days, setDays] = useState(30);
  const insight = useLinkInsight(linkId ?? "", days);

  if (insight.isPending) return <LoadingBlock />;
  if (insight.error) return <Alert>{insight.error.message}</Alert>;

  const { link, sources, trend } = insight.data;

  return (
    <>
      <PageHeader
        title={link.label || link.code}
        description={
          <span className="font-mono break-words">{link.url}</span>
        }
        action={
          <div className="flex flex-wrap items-center gap-2">
            {link.archived && <Tag>{t("archived")}</Tag>}
            <CopyButton value={link.url} />
          </div>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader
            title={t("funnel.title")}
            /*
             * Lifetime, and it says so. The window below changes the sources
             * and the trend but not this: the counts come off the link itself,
             * and an affiliate comparing "clicks in the last 30 days" against
             * "earned ever" would be comparing nothing. Saying which it is
             * costs one line and prevents the whole misreading.
             */
            description={t("funnel.description")}
          />
          <Funnel
            clicks={link.clicks}
            referrals={link.referrals}
            conversions={link.conversions}
            earnedMicros={link.earnedMicros}
          />
        </Card>

        <Card>
          <CardHeader
            title={t("trafficTitle")}
            description={t("trafficDescription")}
            action={<WindowPicker days={days} onDays={setDays} />}
          />
          <SourceTable sources={sources} />
        </Card>
      </div>

      <Card>
        <CardHeader title={t("overTime")} description={t("lastDays", { count: days })} />
        <TrendChart points={trend} series={AFFILIATE_SERIES} />
      </Card>

      <p className="text-[12px] text-fg-subtle">
        <Link
          className="transition-colors duration-200 ease-smooth hover:text-accent"
          to="/links"
        >
          {t("allLinks")}
        </Link>
      </p>
    </>
  );
}

/**
 * The window, and every option is one the API accepts.
 *
 * Seven days is the floor on both sides. A shorter window is one weekend of
 * noise, and an affiliate deciding where to spend next month off three days of
 * data learns the wrong thing confidently.
 */
export function WindowPicker({ days, onDays }: { days: number; onDays: (days: number) => void }) {
  const { t } = useTranslation("links");
  return (
    <Select
      value={days}
      onChange={(event) => onDays(Number(event.target.value))}
      className="h-8 w-36"
      aria-label={t("window")}
    >
      {WINDOWS.map((option) => (
        <option key={option} value={option}>
          {t("windowOption", { count: option })}
        </option>
      ))}
    </Select>
  );
}
